import { Button, Chip, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { getBrandColor } from '@/pages/CustomerDashboard/DashboardLayout';
import { EnergyActivityItem } from '@/pages/CustomerDashboard/Energy/EnergyActivityItem';
import { EnergyPageFrame } from '@/pages/CustomerDashboard/Energy/EnergyPageFrame';
import { EnergyApi } from '@/utilities/api/EnergyApi';
import { ApiEnergyActivityRow, ApiEnergyDashboard } from '@api/interfaces/ApiEnergyDashboard';
import { CircularProgress, Stack } from '@mui/material';
import { formatMediumDate } from '@shared/utilities';
import i18next from 'i18next';
import { groupBy } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';

type ActivityFilter = 'all' | 'earned' | 'redeemed';

const FILTERS: ActivityFilter[] = ['all', 'earned', 'redeemed'];
const PAGE_SIZE = 25;

interface EnergyPointsActivityPageProps {
  accessToken: string,
  data: ApiEnergyDashboard,
}

export const EnergyPointsActivityPage = ({ accessToken, data }: EnergyPointsActivityPageProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const filterLabels: Record<ActivityFilter, string> = {
    all: t(($) => $.customerDashboard.energy.activity.filterAll),
    earned: t(($) => $.customerDashboard.energy.activity.filterEarned),
    redeemed: t(($) => $.customerDashboard.energy.activity.filterRedeemed),
  };

  const { data: pages, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery(
    [QueryKeys.GET_ENERGY_ACTIVITY, accessToken, filter],
    ({ pageParam = 0 }) => (
      EnergyApi.getPointsActivity(accessToken, { page: pageParam, type: filter })
    ),
    {
      getNextPageParam: (lastPage, allPages) => (
        allPages.length * PAGE_SIZE < lastPage.total ? allPages.length : undefined
      ),
    },
  );
  const rows: ApiEnergyActivityRow[] = pages?.pages.flatMap(({ data: pageRows }) => pageRows) ?? [];
  const rowsByDate = groupBy(rows, ({ date }) => date);

  return (
    <EnergyPageFrame
      accessToken={accessToken}
      brandColor={getBrandColor(data.retailer)}
      testID="energy-points-activity-page"
      title={t(($) => $.customerDashboard.pointsActivity.title)}
    >
      <Stack direction="row" gap="8px" p="16px">
        {FILTERS.map((option) => (
          <Chip
            key={option}
            label={filterLabels[option]}
            onClick={() => setFilter(option)}
            testID={`energy-activity-filter-${option}`}
            variant={filter === option ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>
      {!rows.length && !isFetching && (
        <Stack alignItems="center" data-testid="energy-activity-empty" p="32px">
          <Text category="body-large">{t(($) => $.customerDashboard.energy.activity.empty)}</Text>
        </Stack>
      )}
      {Object.entries(rowsByDate).map(([date, dateRows]) => (
        <Stack bgcolor="white" key={date}>
          <Text category="overline" sx={{ p: '12px 16px' }}>
            {formatMediumDate(date, i18next)}
          </Text>
          {dateRows.map((row) => <EnergyActivityItem key={row.id} row={row} />)}
        </Stack>
      ))}
      {isFetching && (
        <Stack alignItems="center" p="16px"><CircularProgress /></Stack>
      )}
      {hasNextPage && !isFetching && (
        <Stack alignItems="center" p="16px">
          <Button onClick={() => fetchNextPage()} testID="energy-activity-load-more" variant="text">
            {t(($) => $.customerDashboard.energy.activity.loadMore)}
          </Button>
        </Stack>
      )}
      <VSpacer size="8" />
    </EnergyPageFrame>
  );
};
